import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "../../api-utility/axiosInstance";

// LOGIN
export const reqToLogin = createAsyncThunk(
  "auth/login",
  async (data, { rejectWithValue }) => {
    try {
      const response = await Axios.post("/auth/login", data);

      if (response.data?.success === false) {
        return rejectWithValue(response.data.message || "Invalid credentials");
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || "Login failed",
      );
    }
  },
);

// LOGOUT
export const reqToLogout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.post("/auth/logout");
      return response.data.success;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || "Logout failed",
      );
    }
  },
);

// UPDATE PRFOFILE
export const reqToUpdateProfile = createAsyncThunk(
  "auth/updateProfile",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Axios.post(`/auth/update-profile`, payload);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// CHANGE PASSWORD
export const reqToChangePassword = createAsyncThunk(
  "auth/changePassword",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Axios.post(`/auth/change-password`, payload);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// ACTIVE DEVICES
export const fetchActiveDevices = createAsyncThunk(
  "auth/fetchActiveDevices",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await Axios.get("/tenant/account/devices", {
        params: userId ? { userId } : {},
      });
      return response.data?.data || {};
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch active devices",
      );
    }
  },
);

export const logoutDeviceSession = createAsyncThunk(
  "auth/logoutDeviceSession",
  async ({ sessionId, userId }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `/tenant/account/devices/${sessionId}/logout`,
        {},
        {
          params: userId ? { userId } : {},
        },
      );
      return {
        sessionId,
        data: response.data?.data || null,
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to logout selected session",
      );
    }
  },
);

// LOGOUT ALL OTHER DEVICES
export const logoutOtherDevices = createAsyncThunk(
  "auth/logoutOtherDevices",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        "/tenant/account/devices/logout-others",
        {},
        {
          params: userId ? { userId } : {},
        },
      );
      return response.data?.data || null;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to logout other devices",
      );
    }
  },
);

// SWITCH ORGANIZATION
export const reqToSwitchOrganization = createAsyncThunk(
  "auth/switchOrganization",
  async (organizationId, { rejectWithValue }) => {
    try {
      const response = await Axios.post("/tenant/organizations/switch", {
        organization_id: organizationId,
      });
      return response.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to switch organization",
      );
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    role: null,
    roleName: null,
    permissions: {},
    token: null,
    tenantSlug: null,
    tenantId: null,
    organizationId: null,
    currentOrganization: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
    activeDevicesLoading: false,
    activeDevicesError: null,
  },

  reducers: {
    resetAuthState: (state) => {
      state.user = null;
      state.role = null;
      state.roleName = null;
      state.permissions = {};
      state.token = null;
      state.tenantSlug = null;
      state.tenantId = null;
      state.organizationId = null;
      state.currentOrganization = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      state.activeDevicesLoading = false;
      state.activeDevicesError = null;
    },
  },

  extraReducers: (builder) => {
    // LOGIN
    builder
      .addCase(reqToLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })

      .addCase(reqToLogin.fulfilled, (state, action) => {
        const payload = action.payload?.data;

        state.user = payload?.user || null;
        state.role = payload?.user?.role_relation || null;
        state.roleName = payload?.user?.role_relation?.name || null;
        state.permissions =
          payload?.user?.role_relation?.permission_config || {};

        state.token = payload?.access_token || null;
        state.tenantId = payload?.user?.tenant_id || null;
        state.organizationId = payload?.user?.current_organization_id || null;
        state.currentOrganization = payload?.user?.current_organization || null;
        state.isAuthenticated = true;
        state.isLoading = false;
        state.error = null;

        if (payload?.access_token) {
          localStorage.setItem("login_token", payload.access_token);
        }

        // Save organization context
        if (payload?.user?.current_organization_id) {
          const authState = {
            tenantId: payload.user.tenant_id,
            organizationId: payload.user.current_organization_id,
          };
          localStorage.setItem("authState", JSON.stringify(authState));
        }
      })

      .addCase(reqToLogin.rejected, (state, action) => {
        state.user = null;
        state.role = null;
        state.roleName = null;
        state.permissions = {};
        state.token = null;
        state.tenantId = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.error = action.payload;
      })

      // LOGOUT
      .addCase(reqToLogout.pending, (state) => {
        state.isLoading = true;
      })

      .addCase(reqToLogout.fulfilled, (state) => {
        state.user = null;
        state.role = null;
        state.roleName = null;
        state.permissions = {};
        state.token = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        localStorage.removeItem("login_token");
      })

      .addCase(reqToLogout.rejected, (state) => {
        state.isLoading = false;
      })

      // UPDATE PRFOFILE
      .addCase(reqToUpdateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })

      .addCase(reqToUpdateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = {
          ...state.user,
          ...action.payload,
        };
      })

      .addCase(reqToUpdateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ACTIVE DEVICES
      .addCase(fetchActiveDevices.pending, (state) => {
        state.activeDevicesLoading = true;
        state.activeDevicesError = null;
      })
      .addCase(fetchActiveDevices.fulfilled, (state) => {
        state.activeDevicesLoading = false;
      })
      .addCase(fetchActiveDevices.rejected, (state, action) => {
        state.activeDevicesLoading = false;
        state.activeDevicesError =
          action.payload || "Failed to fetch active devices";
      })
      .addCase(logoutDeviceSession.pending, (state) => {
        state.activeDevicesLoading = true;
        state.activeDevicesError = null;
      })
      .addCase(logoutDeviceSession.fulfilled, (state) => {
        state.activeDevicesLoading = false;
      })
      .addCase(logoutDeviceSession.rejected, (state, action) => {
        state.activeDevicesLoading = false;
        state.activeDevicesError =
          action.payload || "Failed to logout selected session";
      })

      // LOGOUT OTHER DEVICES
      .addCase(logoutOtherDevices.pending, (state) => {
        state.activeDevicesLoading = true;
        state.activeDevicesError = null;
      })
      .addCase(logoutOtherDevices.fulfilled, (state) => {
        state.activeDevicesLoading = false;
      })
      .addCase(logoutOtherDevices.rejected, (state, action) => {
        state.activeDevicesLoading = false;
        state.activeDevicesError =
          action.payload || "Failed to logout other devices";
      })

      // SWITCH ORGANIZATION
      .addCase(reqToSwitchOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(reqToSwitchOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        
        // Use returned user if available, otherwise update manually
        if (action.payload?.user) {
          state.user = action.payload.user;
          state.currentOrganization = action.payload.user.current_organization || null;
          state.organizationId = action.payload.user.current_organization_id || null;
        } else {
          state.currentOrganization = action.payload?.current_organization || null;
          state.organizationId = action.payload?.current_organization?.id || null;
          state.user = {
            ...state.user,
            current_organization_id: action.payload?.current_organization?.id || null,
            current_organization: action.payload?.current_organization || null,
          };
        }

        // Update organization context in localStorage
        const currentAuthState = JSON.parse(
          localStorage.getItem("authState") || "{}",
        );
        const newAuthState = {
          ...currentAuthState,
          organizationId: action.payload?.current_organization?.id || null,
        };
        localStorage.setItem("authState", JSON.stringify(newAuthState));
      })
      .addCase(reqToSwitchOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { resetAuthState } = authSlice.actions;
export default authSlice.reducer;
